import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { GoalDetail, LogGoalProgressInput } from '@pms/shared';
import { logGoalProgressSchema } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { getErrorMessage } from '@/lib/errors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { GoalProgressBar } from '@/components/goals/GoalProgressBar';
import { formatGoalValue } from '@/components/goals/goalConstants';

interface Props {
  goal: GoalDetail | null;
  onClose: () => void;
}

/**
 * Mirrors `computeProgress` in the backend goals service so the form can preview where an
 * entry lands. The server still owns the stored figure.
 */
function previewPercent(goal: GoalDetail, total: number): number {
  if (goal.target_value === null) return 0;
  const span =
    goal.direction === 'decrease'
      ? goal.start_value - goal.target_value
      : goal.target_value - goal.start_value;
  if (span === 0) return 0;
  const gained =
    goal.direction === 'decrease' ? goal.start_value - total : total - goal.start_value;
  const clamped = Math.min(100, Math.max(0, (gained / span) * 100));
  return Math.round(clamped * 100) / 100;
}

/**
 * Adds one entry to a numeric/percentage/currency goal's running total. The number typed is
 * the movement toward the target, not the new total — seven entries of ₹1,00,000 leave the
 * goal at ₹7,00,000. The server owns the accumulation, and the sign for decrease goals.
 */
export function LogProgressModal({ goal, onClose }: Props) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LogGoalProgressInput>({
    resolver: zodResolver(logGoalProgressSchema),
  });

  useEffect(() => {
    if (!goal) return;
    // No numeric default: the field is an amount to add, so a pre-filled running total
    // would only invite saving it unchanged.
    reset({
      note: null,
      logged_on: new Date().toISOString().slice(0, 10),
    });
  }, [goal, reset]);

  const mutation = useMutation({
    mutationFn: (data: LogGoalProgressInput) => goalsService.logProgress(goal!.id, data),
    onSuccess: () => {
      toast.success('Progress logged');
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const busy = isSubmitting || mutation.isPending;
  const isDecrease = goal?.direction === 'decrease';

  const typed = watch('amount');
  const hasAmount = typeof typed === 'number' && Number.isFinite(typed) && typed !== 0;
  const nextTotal =
    hasAmount && goal ? Math.round((goal.current_value + (isDecrease ? -typed : typed)) * 100) / 100 : null;
  const nextPercent = nextTotal !== null && goal ? previewPercent(goal, nextTotal) : null;

  // Advisory only — the server accepts an overshoot (progress just clamps at 100%) and a
  // negative total, which is legitimate on a goal that starts below zero.
  const target = goal?.target_value ?? null;
  const overshoots =
    nextTotal !== null && target !== null && (isDecrease ? nextTotal < target : nextTotal > target);
  const goesNegative = nextTotal !== null && nextTotal < 0;

  return (
    <Dialog open={!!goal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Progress</DialogTitle>
          <DialogDescription className="line-clamp-2">{goal?.title}</DialogDescription>
        </DialogHeader>

        {goal && (
          <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <GoalProgressBar goal={goal} />
              <p className="mt-2 text-[11px] text-muted-foreground">
                Target {formatGoalValue(goal.target_value, goal.target_type, goal.unit)}
                {isDecrease ? ' (bringing the number down)' : ''}
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="progress-amount">
                {isDecrease ? 'Amount reduced' : 'Amount added'}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="progress-amount"
                type="number"
                step="any"
                autoFocus
                placeholder="0"
                className={errors.amount ? 'border-destructive' : ''}
                onFocus={(e) => e.currentTarget.select()}
                {...register('amount', {
                  setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? NaN : Number(v)),
                })}
              />
              {errors.amount ? (
                <p className="text-xs text-destructive">{errors.amount.message}</p>
              ) : nextTotal !== null ? (
                <p className="text-[11px] text-muted-foreground">
                  {formatGoalValue(goal.current_value, goal.target_type, null)} →{' '}
                  <span className="font-semibold text-foreground">
                    {formatGoalValue(nextTotal, goal.target_type, goal.unit)}
                  </span>{' '}
                  · progress{' '}
                  <span className="font-semibold text-foreground tabular-nums">
                    {goal.progress_percent}% → {nextPercent}%
                  </span>
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  {isDecrease
                    ? 'How much the number came down by — enter it as a positive figure.'
                    : 'How much to add on top of the current total, not the new total.'}
                </p>
              )}
              {(overshoots || goesNegative) && (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  {goesNegative
                    ? 'This takes the total below zero.'
                    : 'This overshoots the target — progress caps at 100%.'}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label>As of</Label>
              <Controller
                control={control}
                name="logged_on"
                render={({ field }) => (
                  <DatePicker
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v || undefined)}
                    placeholder="Today"
                    maxDate={new Date()}
                  />
                )}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="progress-note">Note</Label>
              <Textarea
                id="progress-note"
                rows={3}
                placeholder="What moved the number?"
                {...register('note')}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Update
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
